import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicDataFetch, TABLES } from '../../api';
import SEO from '../../components/SEO';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCalendar, FiUser, FiArrowRight, FiSearch } = FiIcons;
const QUICKBLOG_SCRIPT_ID = 'quickblog-embed-script';
const QUICKBLOG_SCRIPT_SRC = 'https://app.quickblog.co/js/embed.js?qb_id=1003tRUcyiSza27MyqfL1KDo6lUKquaBgybNCF5sxVcf';

function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    const previousScript = document.getElementById(QUICKBLOG_SCRIPT_ID);
    previousScript?.remove();

    const script = document.createElement('script');
    script.id = QUICKBLOG_SCRIPT_ID;
    script.src = QUICKBLOG_SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    const res = await publicDataFetch(`read/${TABLES.POSTS}?status=published&sort=published_at&order=desc`);
    if (res?.status === 'success') {
      setPosts(res.data || []);
    }
    setLoading(false);
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO
        title="Blog | Professionel Indhold & Ekspertise"
        description="Læs de seneste artikler om SEO, AI og digital strategi."
      />

      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Vores Blog</h1>
        <p className="text-xl text-brand-text-muted max-w-2xl mx-auto">
          Dyk ned i vores seneste indsigter, guides og nyheder inden for den digitale verden.
        </p>
      </div>

      <div id="qb-posts"></div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-accent"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group bg-brand-light/5 border border-brand-light/10 rounded-2xl overflow-hidden hover:border-brand-accent/50 transition-all duration-300 flex flex-col h-full"
            >
              <div className="aspect-video relative overflow-hidden">
                <img
                  src={post.featured_image || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=800'}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-brand-accent text-brand-dark text-xs font-bold px-3 py-1 rounded-full">
                  {post.category_name || 'Generelt'}
                </div>
              </div>

              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-xs text-brand-text-muted mb-4">
                  <div className="flex items-center gap-1">
                    <SafeIcon icon={FiCalendar} />
                    {post.published_at ? format(new Date(post.published_at), 'd. MMM yyyy', { locale: da }) : 'Ikke publiceret'}
                  </div>
                  <div className="flex items-center gap-1">
                    <SafeIcon icon={FiUser} />
                    {post.author_name || 'Kasper Wood'}
                  </div>
                </div>

                <h2 className="text-xl font-bold text-white mb-3 group-hover:text-brand-accent transition-colors line-clamp-2">
                  {post.title}
                </h2>

                <p className="text-brand-text-muted text-sm mb-6 line-clamp-3">
                  {post.excerpt}
                </p>

                <div className="mt-auto flex items-center text-brand-accent font-semibold text-sm">
                  Læs mere <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default BlogList;